import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import CustomDT from '../common/controls/customDataTable';
import { Loader } from '../../Common/Loader';
import { colorType3, colorType5, logStyles } from './LogStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import GroupBarChartsComp from '../common/charts/barChart-builder-Grouped';
import { useNav } from '../../../providers/NavProvider';
import LogKpiRow from './LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';

interface Props {
  siteId: string;
  logTitle: string;
}

export default function TrainingLog(props: Props) {
  const [trainingLogData, setTrainingLogData] = useState<null | any>(null);
  const [trainingLogColumns, setTrainingLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  const { sitesResp } = useNav();

  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const accessToken = await getAccessToken();
          const json = await getAllItems(props.logTitle, accessToken, clientAPIURL, props.siteId, undefined, packageSite.id);
          setTrainingLogData(json.items);
          setTrainingLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [sitesResp]);

  return trainingLogData && trainingLogColumns
    ? <CreateTrainingLogCharts LogData={trainingLogData} LogColumns={trainingLogColumns} siteId={props.siteId} logTitle={props.logTitle} clientAPIURL={clientAPIURL} />
    : <Loader />;
}

interface CreateTrainingLogChartsProps {
  LogData: any;
  LogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
}

const CreateTrainingLogCharts: React.FC<CreateTrainingLogChartsProps> = ({ LogData, LogColumns, siteId, logTitle, clientAPIURL }) => {
  const { getAccessToken } = useAuth();
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';

  const ColumnsWithIDColumn = LogColumns && [
    ...LogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    },
  ];

  const updatedData = LogData;
  const [filteredData, setFilteredData] = useState<any[]>(updatedData ?? []);

  useEffect(() => {
    setFilteredData(updatedData ?? []);
  }, [updatedData]);

  async function handleDataUpdate(item: any) {
    if (!item.ID) return;
    try {
      const accessToken = await getAccessToken();
      const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
      const rawItem = result?.items?.[0];
      if (!rawItem) return;
      const formatDate = (v: any): string => {
        if (!v) return v;
        const adj = new Date(new Date(v).getTime() + 4 * 60 * 60000);
        const d = adj.getDate(), m = adj.getMonth() + 1, y = adj.getFullYear();
        return `${d < 10 ? '0' + d : d}/${m < 10 ? '0' + m : m}/${y}`;
      };
      const formatPeriod = (v: any): string | null => {
        if (!v) return null;
        const adj = new Date(new Date(v).getTime() + 4 * 60 * 60000);
        const m = adj.getMonth() + 1, y = adj.getFullYear();
        return `${m < 10 ? '0' + m : m}-${y}`;
      };
      const updatedItem = { ...rawItem, Date: formatDate(rawItem.Date), Period: formatPeriod(rawItem.Date) };
      setFilteredData(prev => [...prev.filter((o: any) => o.ID !== item.ID), updatedItem]);
    } catch (ex) {
      console.error(ex);
    }
  }

  const chartData = useMemo(() => {
    const colorScaleLine = scaleOrdinal(schemeCategory10);
    const isValidNumber = (v: any) => typeof v === 'number' && !isNaN(v) && isFinite(v);

    const uniquePeriod = [...new Set(filteredData.map((item: any) => item.Period))].filter(Boolean) as string[];
    const categoryValues = [...new Set(filteredData.map((item: any) => item.Category))].filter(Boolean) as string[];

    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const conformityData: Record<string, number[]> = {};
    categoryValues.forEach(category => {
      conformityData[category] = uniquePeriod.map(period =>
        filteredData.filter(item => item.Period === period && item.Category === category).length,
      );
    });
    const lineData1 = {
      labels: uniquePeriod,
      datasets: categoryValues.map((category, idx) => ({
        data: conformityData[category],
        strokeWidth: 2,
        color: (_opacity = 1) => colorScaleLine(idx.toString()),
      })),
    };
    const legendData1 = categoryValues.map(name => ({ name }));

    // GroupBarChartsComp — counts per period
    const trainingCounts = uniquePeriod.map(period =>
      filteredData.filter(item => item.Period === period).length,
    );
    const BarData1 = {
      labels: uniquePeriod,
      datasets: [{ data: trainingCounts, strokeWidth: 2, color: (_opacity = 1) => colorScaleLine('0') }],
    };

    // GroupBarChartsComp — attendees per period
    const attendeeCounts = uniquePeriod.map(period =>
      filteredData
        .filter(item => item.Period === period)
        .reduce((acc: number, item: any) => isValidNumber(item.NoOfAttendees) ? acc + item.NoOfAttendees : acc, 0),
    );
    const BarData2 = {
      labels: uniquePeriod,
      datasets: [{ data: attendeeCounts, strokeWidth: 2, color: (_opacity = 1) => colorScaleLine('1') }],
    };

    // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
    const buildMonthPie = (monthPrefix: string, colorFn: (i: string) => string) => {
      const counts: Record<string, number> = {};
      filteredData
        .filter(item => item.Period && item.TrainingSubject != null && item.Period.includes(monthPrefix))
        .forEach((item: any) => {
          const k = String(item.TrainingSubject);
          counts[k] = (counts[k] ?? 0) + 1;
        });
      return Object.entries(counts).map(([name, value], idx) => ({
        name,
        value,
        color: colorFn(idx.toString()),
      }));
    };

    const janPie = buildMonthPie('01', colorType5);
    const febPie = buildMonthPie('02', colorType3);
    const marPie = buildMonthPie('03', colorType5);
    const aprPie = buildMonthPie('04', colorType5);
    const mayPie = buildMonthPie('05', colorType3);
    const junPie = buildMonthPie('06', colorType5);
    const julPie = buildMonthPie('07', colorType5);
    const augPie = buildMonthPie('08', colorType3);
    const sepPie = buildMonthPie('09', colorType5);
    const octPie = buildMonthPie('10', colorType5);
    const novPie = buildMonthPie('11', colorType3);
    const decPie = buildMonthPie('12', colorType5);

    // Summary table
    const periodRows = uniquePeriod.map(period => {
      const items = filteredData.filter(item => item.Period === period);
      return {
        Period: period,
        'No. of Trainings': items.length,
        Attendees: items.reduce((sum: number, item: any) => isValidNumber(item.NoOfAttendees) ? sum + item.NoOfAttendees : sum, 0),
      };
    });
    const columnKeys1 = periodRows.length > 0
      ? Object.keys(periodRows[0]).map((key, index) => ({ ColumnName: key, Order: index }))
      : [];

    // KPI
    const totalAttendees = filteredData.reduce(
      (sum: number, item: any) => isValidNumber(item.NoOfAttendees) ? sum + item.NoOfAttendees : sum, 0,
    );
    const avgAttendees = filteredData.length > 0 ? Math.round(totalAttendees / filteredData.length) : 0;

    const totalDuration = Math.round(
      filteredData.reduce((sum: number, item: any) => sum + (Number(item.DurationMin) || 0), 0),
    );

    const sumByCategory: Record<string, number> = {};
    const durationByCategory: Record<string, number> = {};
    const sumBySubject: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      const cat = item.Category;
      if (cat != null && String(cat) !== 'null' && String(cat) !== 'undefined') {
        sumByCategory[String(cat)] = (sumByCategory[String(cat)] ?? 0) + 1;
        durationByCategory[String(cat)] = (durationByCategory[String(cat)] ?? 0) + (Number(item.DurationMin) || 0);
      }
      const subj = item.TrainingSubject;
      if (subj != null && String(subj) !== 'null' && String(subj) !== 'undefined') {
        sumBySubject[String(subj)] = (sumBySubject[String(subj)] ?? 0) + 1;
      }
    });

    const kpiItems = [
      { label: 'Entries',                 value: filteredData.length, color: '#4527A0' },
      { label: 'Total Attendees',         value: totalAttendees,       color: '#1565C0' },
      { label: 'Avg Attendees / Session', value: avgAttendees,         color: '#00695C' },
      { label: 'Total Duration (min)',    value: totalDuration,         color: '#E65100' },
      ...Object.entries(sumByCategory).map(([name, value], idx) => ({
        label: name,
        value,
        color: colorType5(idx.toString()),
      })),
      ...Object.entries(durationByCategory).map(([name, value], idx) => ({
        label: `${name} (min)`,
        value: Math.round(value),
        color: colorType3(idx.toString()),
      })),

    ];

    return {
      uniquePeriod,
      lineData1, legendData1,
      BarData1, BarData2,
      janPie, febPie, marPie, aprPie, mayPie, junPie,
      julPie, augPie, sepPie, octPie, novPie, decPie,
      periodRows, columnKeys1,
      kpiItems,
    };
  }, [filteredData]);

  const {
    uniquePeriod,
    lineData1, legendData1,
    BarData1, BarData2,
    janPie, febPie, marPie, aprPie, mayPie, junPie,
    julPie, augPie, sepPie, octPie, novPie, decPie,
    periodRows, columnKeys1,
    kpiItems,
  } = chartData;

  const kpiRow = (
    <View style={{ zIndex: 200 }}>
      <LogKpiRow items={kpiItems}>
        <LogDateRangeFilter data={updatedData} onFilterChange={setFilteredData} />
      </LogKpiRow>
    </View>
  );

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab === 'list' ? 2 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: '100%' }} showLeadingSpace={true}>
          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              {kpiRow}
              <View style={logStyles.container}>
                {/* LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=fn */}
                <View style={[logStyles.card, { backgroundColor: '' }]}>
                  <LineChartScreen legend={legendData1} data={lineData1} label={uniquePeriod} ChartTitle={'Training Category'} subTitle={'(Monthly)'} />
                </View>
                <View style={[logStyles.card, { backgroundColor: '' }]}>
                  <GroupBarChartsComp data={BarData1} label={uniquePeriod} ChartTitle={'Number of Trainings'} subTitle={'(Monthly)'} />
                </View>
                <View style={[logStyles.card, { backgroundColor: '' }]}>
                  <GroupBarChartsComp data={BarData2} label={uniquePeriod} ChartTitle={'Number of Attendees'} subTitle={'(Monthly)'} />
                </View>

                {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={janPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Jan'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={febPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Feb'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={marPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Mar'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={aprPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Apr'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={mayPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - May'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={junPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Jun'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={julPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Jul'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={augPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Aug'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={sepPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Sep'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={octPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Oct'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={novPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Nov'} /></View>
                <View style={[logStyles.card, { backgroundColor: '' }]}><PieChartComp data={decPie} colorPallate={interpolateBlues} ChartTitle={'Training Subjects % - Dec'} /></View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Tables" icon="table">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={logStyles.container}>
                <View style={logStyles.cardThree}>
                  {periodRows.length > 0 && columnKeys1.length > 0 && (
                    <CustomDT
                      data={periodRows}
                      columns={columnKeys1}
                      siteId={siteId}
                      logTitle={logTitle}
                      tableId={'TrainingsAttendees'}
                      tableTitle={'No. of Trainings & No. of Attendees'}
                    />
                  )}
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={logStyles.container}>
                <View style={logStyles.cardThree}>
                  {filteredData && ColumnsWithIDColumn && (
                    <PaperTableDynamic
                      data={filteredData}
                      columns={ColumnsWithIDColumn}
                      siteId={siteId}
                      logTitle={logTitle}
                      updateData={handleDataUpdate}
                      isConsolidatedTable={false}
                    />
                  )}
                </View>
              </View>
            </ScrollView>
          </TabScreen>
        </Tabs>
      </TabsProvider>
    </View>
  );
};
